import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  formatDuration,
  MAX_VOICE_MS,
  primeVoiceNotePeaks,
  queryMicPermission,
  startVoiceRecording,
  uploadVoiceNote,
  type VoiceRecorder,
  type VoiceRef,
} from "../game/voice-note";
import { fa } from "../i18n/fa";
import { GameIcon } from "./GameIcon";

type Mode = "idle" | "recording" | "uploading";

/** Rolling live-waveform strip: how many amplitude bars are kept on screen,
 * and how often the mic meter is sampled (~12 fps — smooth without burning
 * the WebView; the spec range is 10–15 fps). */
export const REC_WAVE_BARS = 32;
export const REC_WAVE_INTERVAL_MS = 80;

function MicGlyph({ size = 18 }: { size?: number }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="block"
    >
      <rect x="9" y="2.5" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </svg>
  );
}

function CloseGlyph(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width={19}
      height={19}
      fill="none"
      stroke="currentColor"
      strokeWidth={3.4}
      strokeLinecap="round"
      aria-hidden="true"
      className="block"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/**
 * Branded, on-theme pre-prompt shown BEFORE the native mic dialog on the FIRST voice
 * recording — a clean RTL card explaining why we need the mic, so the OS prompt isn't
 * jarring and accidental denials drop. «اجازه دادن» then calls getUserMedia (which fires
 * the native dialog, whose look/text/domain we can't change); dismiss backs out. All
 * design tokens → light + dark automatically. Not shown once the mic is already granted.
 */
function MicPrePrompt({
  onAllow,
  onDismiss,
}: {
  onAllow: () => void;
  onDismiss: () => void;
}): ReactNode {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={fa.chat.micAskTitle}
      className="fixed inset-0 z-[80] grid place-items-center p-6"
      style={{ background: "rgba(0,0,0,.55)", backdropFilter: "blur(2px)" }}
      onClick={onDismiss}
    >
      <div
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[320px] rounded-2xl border border-outline-variant bg-surface-container p-5 text-center shadow-2xl"
        style={{ animation: "bg-pop 0.28s ease" }}
      >
        <span className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-secondary/15 text-secondary">
          <MicGlyph size={24} />
        </span>
        <h3 className="m-0 mb-1.5 font-headline-sm text-headline-sm text-on-surface">
          {fa.chat.micAskTitle}
        </h3>
        <p className="m-0 mb-2 font-body-md text-body-md text-on-surface-variant">
          {fa.chat.micAskBody}
        </p>
        <p className="m-0 mb-4 font-caption text-caption leading-snug text-on-surface-variant/80">
          {fa.chat.micAskHint}
        </p>
        <button
          type="button"
          onClick={onAllow}
          className="mb-2 w-full cursor-pointer rounded-xl border-0 bg-secondary py-2.5 font-label-md text-label-md font-bold text-ink-on-gold shadow-md transition-transform active:scale-95"
        >
          {fa.chat.micAskAllow}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="w-full cursor-pointer rounded-xl border border-outline-variant bg-transparent py-2 font-label-md text-label-md font-bold text-on-surface-variant transition-colors hover:text-on-surface"
        >
          {fa.chat.micAskLater}
        </button>
      </div>
    </div>
  );
}

/**
 * The composer's mic control — the whole record → send/cancel → upload flow in one
 * reusable piece (shared by the DM thread and the table chat). Idle it's a round mic
 * button; while recording it expands into a LIVE amplitude-bar strip (the recorder's
 * WebAudio meter sampled ~12 fps into a rolling waveform; WebViews without WebAudio
 * keep the pulsing-dot fallback) plus a mm:ss timer with cancel (✕) and send (✓); on
 * send it uploads via {@link uploadVoiceNote} and calls `onSendVoice` with the stored
 * id. Auto-stops (and sends) at {@link MAX_VOICE_MS}. `onActiveChange` lets the
 * composer hide its text input while a recording is in progress. Stitch-tokened UI.
 */
export function MicButton({
  onSendVoice,
  onActiveChange,
  disabled,
}: {
  onSendVoice: (ref: VoiceRef) => void;
  onActiveChange?: (active: boolean) => void;
  disabled?: boolean;
}): ReactNode {
  const [mode, setMode] = useState<Mode>("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // The rolling live-amplitude strip (0..1 per bar); [] until samples land.
  const [bars, setBars] = useState<readonly number[]>([]);
  const recorderRef = useRef<VoiceRecorder | null>(null);
  const meterRef = useRef<(() => number) | null>(null);
  const startAtRef = useRef(0);
  const mountedRef = useRef(true);
  const sendRef = useRef<() => void>(() => {});
  const [showPrePrompt, setShowPrePrompt] = useState(false);
  // Skip the pre-prompt when the origin already holds the mic grant (persisted in a real
  // browser, or granted earlier this session). Many WebViews can't answer permissions.query
  // for "microphone" → `granted` stays false and the pre-prompt shows once, before the
  // native dialog — which is correct (the grant isn't there yet).
  const [granted, setGranted] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      recorderRef.current?.cancel();
      recorderRef.current = null;
    };
  }, []);

  // Best-effort: pre-read the grant so a first tap can skip our pre-prompt when it's
  // already allowed. (null in WebViews without permissions.query for the mic → pre-prompt.)
  useEffect(() => {
    void queryMicPermission().then((state) => {
      if (state === "granted" && mountedRef.current) setGranted(true);
    });
  }, []);

  // Auto-dismiss a transient error so the composer settles back to the mic.
  useEffect(() => {
    if (!error) return;
    const id = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(id);
  }, [error]);

  const finish = (active: boolean): void => {
    meterRef.current = null;
    setMode("idle");
    onActiveChange?.(active);
  };

  const send = (): void => {
    const rec = recorderRef.current;
    if (!rec) return;
    recorderRef.current = null;
    setMode("uploading");
    void rec
      .stop()
      .then(({ blob, durationMs }) =>
        uploadVoiceNote(blob, durationMs).then((ref) => ({ ref, blob })),
      )
      .then(({ ref, blob }) => {
        // Decode the recorded clip locally so the sender's own bubble paints the
        // real waveform the instant it appears (and plays back from this blob —
        // no re-download). Best-effort + memoized per id; a decode failure just
        // leaves the bubble to fetch lazily.
        void primeVoiceNotePeaks(ref.voiceId, blob);
        if (!mountedRef.current) return;
        onSendVoice(ref);
        finish(false);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setError(fa.chat.voiceSendError);
        finish(false);
      });
  };
  sendRef.current = send;

  const cancel = (): void => {
    recorderRef.current?.cancel();
    recorderRef.current = null;
    finish(false);
  };

  const start = (): void => {
    if (disabled || mode !== "idle") return;
    setError(null);
    void startVoiceRecording()
      .then((rec) => {
        if (!mountedRef.current) {
          rec.cancel();
          return;
        }
        recorderRef.current = rec;
        // Live meter for the waveform strip — absent (no WebAudio) the
        // recording row keeps its pulsing-dot fallback.
        meterRef.current = rec.meter ? rec.meter.read : null;
        // Baseline-fill so the waveform spans the FULL width from the first frame (not
        // just a few bars in the middle); real samples then roll in from the right.
        setBars(new Array(REC_WAVE_BARS).fill(0));
        startAtRef.current = performance.now();
        setElapsedMs(0);
        setMode("recording");
        onActiveChange?.(true);
        setGranted(true); // grant went through → skip the pre-prompt next time this session
      })
      .catch(() => {
        if (mountedRef.current) setError(fa.chat.micError);
      });
  };

  // The mic tap: record straight away if the mic is already granted (or live this
  // session); otherwise show our branded pre-prompt first — its «اجازه دادن» button is
  // the user gesture that then calls getUserMedia (→ the native dialog).
  const requestStart = (): void => {
    if (disabled || mode !== "idle") return;
    if (granted) start();
    else setShowPrePrompt(true);
  };

  // Live timer + safety auto-send at the cap (the recorder also self-stops there).
  useEffect(() => {
    if (mode !== "recording") return;
    const id = setInterval(() => {
      const elapsed = performance.now() - startAtRef.current;
      setElapsedMs(elapsed);
      if (elapsed >= MAX_VOICE_MS) sendRef.current();
    }, 200);
    return () => clearInterval(id);
  }, [mode]);

  // Live waveform sampling — a rolling strip of the newest amplitude reads.
  useEffect(() => {
    if (mode !== "recording") return;
    const read = meterRef.current;
    if (!read) return;
    const id = setInterval(() => {
      setBars((prev) => [...prev.slice(-(REC_WAVE_BARS - 1)), read()]);
    }, REC_WAVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [mode]);

  if (mode === "uploading") {
    return (
      <div
        role="status"
        className="flex flex-1 items-center gap-2.5 px-1.5 font-body-md text-body-md font-bold text-on-surface-variant"
      >
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-secondary/30 border-t-secondary"
        />
        {fa.chat.sending}
      </div>
    );
  }

  if (mode === "recording") {
    const hasMeter = meterRef.current !== null;
    return (
      <div className="flex flex-1 items-center gap-2 rounded-full border border-outline-variant bg-surface-container-low px-1.5 py-1">
        {/* SEND — swapped to the START (was at the end). */}
        <button
          type="button"
          aria-label={fa.chat.sendVoice}
          onClick={send}
          className="grid h-9 w-9 flex-none cursor-pointer place-items-center rounded-full border-0 bg-secondary text-ink-on-gold"
        >
          <GameIcon type="check" size={17} />
        </button>
        <span
          aria-hidden="true"
          className="h-2.5 w-2.5 flex-none animate-pulse rounded-full bg-error"
        />
        {hasMeter ? (
          /* The live rolling waveform — the bars STRETCH to fill the full strip width
             (flex-1), baseline-filled on start so it's never just a stub in the middle.
             LTR pin keeps the growth direction stable inside the RTL composer. */
          <span
            aria-hidden="true"
            data-testid="rec-wave"
            dir="ltr"
            className="flex h-6 min-w-0 flex-1 items-center gap-[2px] overflow-hidden px-1"
          >
            {bars.map((a, i) => (
              <span
                key={i}
                className="min-w-0 flex-1 rounded-full bg-error"
                style={{
                  height: `${Math.round(Math.max(0.14, a) * 100)}%`,
                  opacity: 0.45 + a * 0.55,
                  transition: "height 0.08s linear",
                }}
              />
            ))}
          </span>
        ) : (
          <span aria-hidden="true" className="min-w-0 flex-1" />
        )}
        <span className="flex-none font-body-md text-body-md font-extrabold tabular-nums text-on-surface">
          {formatDuration(elapsedMs)}
        </span>
        {/* CANCEL — swapped to the END; red-tinted + a bolder ✕ so it's obvious. */}
        <button
          type="button"
          aria-label={fa.chat.cancelVoice}
          onClick={cancel}
          className="grid h-9 w-9 flex-none cursor-pointer place-items-center rounded-full border-0 bg-error/15 text-error"
        >
          <CloseGlyph />
        </button>
      </div>
    );
  }

  // idle — a mic button, plus a transient error bubble above it if one occurred.
  return (
    <span className="relative inline-flex flex-none">
      {error ? (
        <span
          role="alert"
          className="absolute bottom-[calc(100%+8px)] whitespace-nowrap rounded-lg border border-outline-variant bg-surface-container px-2.5 py-1.5 text-caption font-bold text-error shadow-lg"
          style={{ insetInlineEnd: 0 }}
        >
          {error}
        </span>
      ) : null}
      <button
        type="button"
        aria-label={fa.chat.recordVoice}
        onClick={requestStart}
        disabled={disabled}
        className={`grid h-11 w-11 flex-none place-items-center rounded-full border border-outline-variant bg-surface-container-high text-secondary transition-transform active:scale-95 ${
          disabled ? "cursor-default opacity-50" : "cursor-pointer hover:bg-surface-bright/20"
        }`}
      >
        <MicGlyph />
      </button>
      {showPrePrompt ? (
        <MicPrePrompt
          onAllow={() => {
            setShowPrePrompt(false);
            start();
          }}
          onDismiss={() => setShowPrePrompt(false)}
        />
      ) : null}
    </span>
  );
}
